import { useCallback, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanItem,
  KanbanOverlay,
} from '@/components/ui/kanban'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  TASK_STATUS_LABELS,
  type TaskRow,
  type TaskStatus,
} from '@/types/work'
import { TaskKanbanCard } from './TaskKanbanCard'
import {
  KANBAN_COLUMNS,
  groupTasksForBoard,
  type BoardColumns,
  type BoardItem,
} from './kanban-grouping'

// Sprint 5b U8: the D6-block error from CrmWork::UpdateTaskStatus
// surfaces as a flash alert prefixed with this string + a [task_id=N]
// suffix so the toast renderer can render an action button linking
// back to the parent's Show page. Plan DR-T2-E rejected the structured-
// error refactor; this prefix-match is the lightweight alternative.
const SUBTASKS_OPEN_ALERT_PREFIX = 'Sous-tâches ouvertes'
const SUBTASKS_OPEN_TASK_ID_REGEX = /\[task_id=(\d+)\]/

// Plan DR-T1-B: Archivé is non-droppable (admin-only via the Show page).
// Annulé became droppable in Phase 3 U7 — drops there route through a
// confirm dialog before firing the PATCH.
const NON_DROPPABLE_COLUMNS: ReadonlySet<TaskStatus> = new Set(['archived'])
// Cards in cancelled / archived columns are read-only (TaskPolicy#
// update_status? denies status changes from closed tasks), so dnd-kit
// shouldn't even start a drag from them.
const READ_ONLY_SOURCE_COLUMNS: ReadonlySet<TaskStatus> = new Set(['archived', 'cancelled'])
// Drops that need a confirm dialog before any PATCH fires (U7).
const CONFIRM_BEFORE_DROP_COLUMNS: ReadonlySet<TaskStatus> = new Set(['cancelled'])

const COLUMN_DOT_CLASS: Record<TaskStatus, string> = {
  pending: 'bg-slate-400',
  in_progress: 'bg-blue-500',
  on_hold: 'bg-amber-500',
  completed: 'bg-emerald-500',
  cancelled: 'bg-rose-400',
  archived: 'bg-zinc-400',
}

// Within-column sort. Server already orders open columns by due_at and
// closed columns by updated_at (TasksController U2). The board layers
// the documented user-facing precedence on top: priority desc, then
// due_at asc with NULLs last, then created_at desc as a tiebreaker.
const PRIORITY_RANK: Record<TaskRow['priority'], number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
}

function compareTasks(a: TaskRow, b: TaskRow): number {
  const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
  if (p !== 0) return p

  if (a.due_at && b.due_at) {
    const d = new Date(a.due_at).getTime() - new Date(b.due_at).getTime()
    if (d !== 0) return d
  } else if (a.due_at) {
    return -1
  } else if (b.due_at) {
    return 1
  }

  // No id-asc/desc preference in plan; use parent_task_id null-first then
  // id desc so the newest tasks bubble up within a tie.
  return b.id - a.id
}

function sortBoardItems(items: BoardItem[]): BoardItem[] {
  return [...items].sort((a, b) => compareTasks(a.task, b.task))
}

// Each column renders a flat ordered list of dnd-kit items (parents +
// standalone-orphans). We need a `getItemValue` that returns the task
// id regardless of which BoardItem variant is involved.
function getItemId(item: BoardItem): number {
  return item.task.id
}

interface TasksKanbanBoardProps {
  tasks: TaskRow[]
  onTaskClick?: (id: number) => void
}

export function TasksKanbanBoard({ tasks, onTaskClick }: TasksKanbanBoardProps) {
  // Recompute the column buckets every time `tasks` changes — the
  // grouping rule is purely a function of the latest task list (plan
  // §Subtree grouping). Local state only exists for the optimistic-drag
  // window; once the Inertia reload arrives we re-derive from props.
  const initial = useMemo(() => {
    const grouped = groupTasksForBoard(tasks)
    const sorted: BoardColumns = {} as BoardColumns
    for (const col of KANBAN_COLUMNS) sorted[col] = sortBoardItems(grouped[col])
    return sorted
  }, [tasks])

  const [columns, setColumns] = useState<BoardColumns>(initial)
  // Track the last `initial` reference we synced from. When `tasks` prop
  // changes, useMemo gives a new `initial` object, the ref-mismatch
  // fires once, and we reset both. This is the React-endorsed "setState
  // during render based on a prop change" pattern — avoids the
  // one-frame flash a useEffect-based sync would produce after every
  // Inertia partial reload following a drag.
  const [lastInitial, setLastInitial] = useState(initial)
  if (initial !== lastInitial) {
    setLastInitial(initial)
    setColumns(initial)
  }

  const snapshotRef = useRef<BoardColumns | null>(null)
  const [activeTask, setActiveTask] = useState<TaskRow | null>(null)

  // U7: pending Annulé drag awaiting confirmation. Holds the snapshot
  // for restore-on-cancel and the destination/task data for the
  // commit-on-confirm path.
  const [pendingCancel, setPendingCancel] = useState<{
    taskId: number
    destStatus: TaskStatus
    snapshot: BoardColumns
  } | null>(null)

  const performStatusPatch = useCallback(
    (taskId: number, destStatus: TaskStatus, snapshot: BoardColumns) => {
      router.patch(
        // ?source=kanban drives SC1/SC2 telemetry through the existing
        // controller allowlist (DR-T1-C, shipped in Phase 1).
        `/work/tasks/${taskId}/update_status?source=kanban`,
        { status: destStatus },
        {
          preserveScroll: true,
          preserveState: true,
          onSuccess: (page) => {
            // Plan §Drag-to-move state machine: alert means denial → roll
            // back. notice (including the D6 cascade auto-complete
            // notice) means commit → keep optimistic state.
            const flash = (page.props as { flash?: { alert?: string } }).flash
            const alertText = flash?.alert
            if (!alertText) return

            // U8: subtasks-open block surfaces as an alert prefixed with
            // SUBTASKS_OPEN_ALERT_PREFIX + a [task_id=N] suffix. Render
            // the toast with an action linking to the parent's Show page
            // so users can find the open subtasks without hunting.
            setColumns(snapshot)
            if (alertText.startsWith(SUBTASKS_OPEN_ALERT_PREFIX)) {
              const match = alertText.match(SUBTASKS_OPEN_TASK_ID_REGEX)
              const cleanText = alertText.replace(SUBTASKS_OPEN_TASK_ID_REGEX, '').trim()
              toast.error(cleanText, {
                action: match
                  ? {
                      label: 'Voir les sous-tâches',
                      onClick: () => router.visit(`/work/tasks/${match[1]}`),
                    }
                  : undefined,
              })
            }
            // For other alerts (auth denial, etc) the global useFlash
            // hook already emits a toast.error — don't double-fire.
          },
          onError: () => {
            setColumns(snapshot)
          },
        },
      )
    },
    [],
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      snapshotRef.current = structuredClone(columns)
      const id = event.active.id
      const found = findTask(columns, id)
      if (found) setActiveTask(found)
    },
    [columns],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveTask(null)
      const snapshot = snapshotRef.current
      snapshotRef.current = null
      if (!snapshot) return

      // Drop happened outside any droppable (drag-cancel via Esc, drop
      // off-board, etc). The primitive's onDragOver already mutated
      // `columns` optimistically while the user dragged — restore the
      // snapshot so the cancelled drag doesn't leave a stale move on
      // screen until the next reload (PR #96 P2).
      if (!event.over) {
        setColumns(snapshot)
        return
      }

      // The primitive's onDragOver already moved the item into the
      // destination column by drop time. Read source from snapshot,
      // destination from current state.
      const sourceCol = findColumnOf(snapshot, event.active.id)
      const destCol = findColumnOf(columns, event.active.id)
      if (!sourceCol || !destCol) return

      // Within-column drag: the primitive applied dnd-kit's reorder via
      // onDragOver, but plan §Scope Boundaries explicitly excludes
      // within-column reordering ("sort is server-deterministic"). Snap
      // back to the snapshot so the documented priority/due-date sort
      // is preserved without waiting for the next reload (PR #96 P2).
      if (sourceCol === destCol) {
        setColumns(snapshot)
        return
      }

      // Drag FROM a closed column: TaskPolicy#update_status? returns
      // false for cancelled/archived tasks. Short-circuit locally — no
      // server roundtrip, no toast for a predictable-deny case.
      if (READ_ONLY_SOURCE_COLUMNS.has(sourceCol)) {
        setColumns(snapshot)
        return
      }

      // Drop INTO a non-droppable column (Archivé in 5b): snap back
      // without firing a PATCH.
      if (NON_DROPPABLE_COLUMNS.has(destCol)) {
        setColumns(snapshot)
        return
      }

      const taskId = Number(event.active.id)

      // U7: drops into Annulé hold the optimistic move pending until
      // the user confirms via AlertDialog. Snap back to origin while
      // the dialog is open so the card visibly stays put.
      if (CONFIRM_BEFORE_DROP_COLUMNS.has(destCol)) {
        setColumns(snapshot)
        setPendingCancel({ taskId, destStatus: destCol, snapshot })
        return
      }

      performStatusPatch(taskId, destCol, snapshot)
    },
    [columns, performStatusPatch],
  )

  function confirmCancelDrag() {
    if (!pendingCancel) return
    const { taskId, destStatus, snapshot } = pendingCancel
    setPendingCancel(null)
    // Re-apply the optimistic move now that the user confirmed.
    setColumns((prev) => {
      const next: BoardColumns = { ...prev }
      for (const status of KANBAN_COLUMNS) {
        next[status] = next[status].filter((it) => it.task.id !== taskId)
      }
      const item = findBoardItem(snapshot, taskId)
      if (item) {
        const moved: BoardItem =
          item.type === 'parent-with-nested'
            ? { ...item, task: { ...item.task, status: destStatus } }
            : { ...item, task: { ...item.task, status: destStatus } }
        next[destStatus] = [...next[destStatus], moved]
      }
      return next
    })
    performStatusPatch(taskId, destStatus, snapshot)
  }

  function dismissCancelDrag() {
    setPendingCancel(null)
    // Snapshot is already restored when the dialog opened — nothing else.
  }

  return (
    <Kanban
      value={columns}
      onValueChange={(val) => setColumns(val as BoardColumns)}
      getItemValue={getItemId}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <KanbanBoard className="grid gap-3 grid-cols-1 md:grid-cols-3 xl:grid-cols-6">
        {KANBAN_COLUMNS.map((status) => {
          const items = columns[status] ?? []
          // Visual dimming for the read-only / non-droppable columns.
          // Annulé is droppable (gated by U7 confirm) so it stays at full
          // opacity. Archivé is non-droppable in 5b (admin-only).
          const visuallyClosed = status === 'archived'
          // Cards in closed columns can't be dragged out — policy denies
          // status changes from cancelled/archived tasks.
          const readOnlyCard = READ_ONLY_SOURCE_COLUMNS.has(status)

          return (
            <KanbanColumn
              key={status}
              value={status}
              className={`min-h-[200px] rounded-xl border-0 bg-muted/40 ${visuallyClosed ? 'opacity-75' : ''}`}
            >
              <div className="flex items-center justify-between px-2.5 pb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${COLUMN_DOT_CLASS[status]}`} />
                  <h3 className="truncate text-xs font-semibold uppercase tracking-wide text-foreground/80">
                    {TASK_STATUS_LABELS[status]}
                  </h3>
                </div>
                <Badge variant="secondary" className="shrink-0 text-[10px]">
                  {items.length}
                </Badge>
              </div>

              <div className="flex flex-col gap-2 px-1.5 pb-2">
                {items.length === 0 ? (
                  <EmptyColumnDropZone />
                ) : (
                  items.map((item) => (
                    <KanbanItem
                      key={item.task.id}
                      value={item.task.id}
                      // Whole-card drag activator (Jira-style). Sensor
                      // activation distance (8px) in the primitive means
                      // a tap-without-movement is a click, not a drag.
                      asHandle={!readOnlyCard}
                      // Cards in closed columns are non-draggable: the
                      // policy denies status changes anyway and dnd-kit
                      // shouldn't even start a drag. The primitive's
                      // `disabled` adds `pointer-events-none` which
                      // would kill click handlers, but cn() uses
                      // tailwind-merge so passing pointer-events-auto
                      // deterministically wins on the cascade.
                      disabled={readOnlyCard}
                      className={[
                        'cursor-pointer',
                        readOnlyCard ? 'pointer-events-auto opacity-100' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => onTaskClick?.(item.task.id)}
                    >
                      <TaskKanbanCard
                        task={item.task}
                        nestedChildren={item.type === 'parent-with-nested' ? item.nestedChildren : undefined}
                        parentReference={item.type === 'child-with-parent-frame' ? item.parent : undefined}
                        onTaskClick={onTaskClick}
                      />
                    </KanbanItem>
                  ))
                )}
              </div>
            </KanbanColumn>
          )
        })}
      </KanbanBoard>

      <KanbanOverlay>
        {activeTask ? (
          <TaskKanbanCard task={activeTask} isDragging className="opacity-90" />
        ) : null}
      </KanbanOverlay>

      {/* U7 — confirm dialog for drag-into-Annulé. Open is bound to
          pendingCancel; closing via Esc / click-outside dismisses
          without firing a PATCH. */}
      <AlertDialog
        open={pendingCancel !== null}
        onOpenChange={(open) => {
          if (!open) dismissCancelDrag()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler cette tâche ?</AlertDialogTitle>
            <AlertDialogDescription>
              La tâche sera marquée comme annulée. Vous pourrez la consulter via le filtre Statut.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={dismissCancelDrag}>Conserver</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelDrag}>Annuler la tâche</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Kanban>
  )
}

// U10 — empty-column placeholder. Slim dashed drop-target so the column
// still reads as a valid drop zone when zero rows are present.
function EmptyColumnDropZone() {
  return (
    <div className="flex h-[100px] items-center justify-center rounded-lg border border-dashed border-muted-foreground/20">
      <p className="text-[11px] text-muted-foreground/60">Aucune tâche</p>
    </div>
  )
}

function findBoardItem(columns: BoardColumns, id: number): BoardItem | null {
  for (const status of KANBAN_COLUMNS) {
    const found = columns[status].find((it) => it.task.id === id)
    if (found) return found
  }
  return null
}

// ---------- helpers ----------

function findColumnOf(columns: BoardColumns, id: string | number): TaskStatus | null {
  for (const status of KANBAN_COLUMNS) {
    if (columns[status].some((it) => it.task.id === Number(id))) return status
  }
  return null
}

function findTask(columns: BoardColumns, id: string | number): TaskRow | null {
  for (const status of KANBAN_COLUMNS) {
    const it = columns[status].find((bi) => bi.task.id === Number(id))
    if (it) return it.task
  }
  return null
}
